import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Decision } from '_gql'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'
import { GetCreditApplicationTilaInfo_creditApplication_CreditApplication as CreditApplicationType } from '@sputnik/pages/PostApplicationPages/ApplicationAgreements/GetCreditApplicationTilaInfo.gql'
import { useGetAuthSignerEmail } from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/GetAuthSignerEmail.gql'
import { useGetTilaInfo } from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/GetTilaInfo.gql'

import useGetIsLoggedInAsAuthSigner from './useGetIsLoggedInAsAuthSigner'

/* this hook is used in CreditLineOfferPage && EligibleOfferTile to avoid cluttering up the component and having to maintain duplicate code in 3 places and the components that use it (PrepayOfferPage, StandardLineCreditPage, VariableLineCreditPage)
 */

interface useGetInfoForApplicationAgreementsProps {
  decision: Pick<Decision, 'id' | 'approvedAmountInCents' | 'offerType'> | undefined | null
  isLoadingAcceptingOffer: boolean
  hasAgreedToTerms: boolean
  skip?: boolean
}

const useGetInfoForApplicationAgreements = ({
  decision,
  hasAgreedToTerms,
  isLoadingAcceptingOffer,
  skip = false,
}: useGetInfoForApplicationAgreementsProps) => {
  const { appId } = useParams()
  const { id: decisionId, offerType } = decision || {}
  const { data: authSignerData, loading: isLoadingAuthSignerData } = useGetAuthSignerEmail({
    skip: skip || !appId,
    variables: {
      creditApplicationId: appId as string,
    },
  })

  const authEmail = authSignerData?.creditApplication?.authorizedSigner?.email || ''

  const { isLoggedInUserAuthorizedSigner } = useGetIsLoggedInAsAuthSigner({
    authSignerEmail: authEmail,
  })

  const {
    data,
    loading: isLoadingTilaInfo,
    error: tilaInfoError,
  } = useGetTilaInfo({
    fetchPolicy: 'network-only',
    variables: {
      creditApplicationId: appId as string,
      underwritingDecisionUuid: decisionId,
      underwritingOfferType: offerType,
      skipTila: !isLoggedInUserAuthorizedSigner,
    },
    // we don't need any of this information if the user is not the authorized signer
    skip: skip || !isLoggedInUserAuthorizedSigner || !appId || !decision,
  })

  useApolloQueryErrorHandling({
    error: tilaInfoError,
    logging: {
      eventName: 'GetTilaInfo',
      message: 'Error fetching TILA info for application',
      attributes: {
        creditAppId: appId,
      },
    },
  })

  const isLoading = isLoadingAuthSignerData || isLoadingTilaInfo || isLoadingAcceptingOffer

  const creditApplication = useMemo(
    () => (data?.creditApplication as CreditApplicationType) || {},
    [data?.creditApplication],
  )

  const tila: { content?: string; meta?: { signedAt?: string } } = creditApplication?.tila || {}

  const hasTilaContent = !!tila?.content
  const hasAgreedToTila = !!tila?.meta?.signedAt
  const disabledWithTila = !hasAgreedToTerms || isLoading || (hasTilaContent && !hasAgreedToTila) || !creditApplication
  const disabledWithoutTila = !hasAgreedToTerms || isLoading
  const disableAcceptOfferButton = hasTilaContent ? disabledWithTila : disabledWithoutTila

  return {
    isLoggedInUserAuthorizedSigner,
    authEmail,
    disableAcceptOfferButton,
    hasAgreedToTila,
    creditApplication,
    isLoading,
    tilaInfoError,
    hasTilaContent,
  }
}

export default useGetInfoForApplicationAgreements
